// usePracticeUsers.js - one shared read of the practice's staff list.
//
// Round 6 item 10: the Owner dashboard read `users` four times on one load.
// Two of those were the same list asked twice: the worklist's assign picker
// (useAssignableStaff) and its "Claimed: Casey" names (WorklistPanel). Both
// now read this one TanStack Query entry (the existing query-cache context,
// as usePracticePreferences does), so any number of cards on a screen share
// one request. Cleared on sign-out with the rest of the cache (AuthProvider).
//
// Returns every user row of the practice (active or not, so a claim by
// someone who has since left still shows a name); callers filter. Staff names
// and roles only, no patient data.
import { useQuery } from "@tanstack/react-query";
import { supabase } from "./supabaseClient";

const EMPTY = Object.freeze([]);

export const practiceUsersKey = (practiceId) => ["practice-users", practiceId ?? null];

async function fetchPracticeUsers(practiceId) {
  const { data, error } = await supabase.from("users")
    .select("id, full_name, role, is_active")
    .eq("practice_id", practiceId)
    .order("full_name");
  if (error) throw error;
  return data || [];
}

export default function usePracticeUsers(practiceId, enabled = true) {
  const q = useQuery({
    queryKey: practiceUsersKey(practiceId),
    queryFn: () => fetchPracticeUsers(practiceId),
    enabled: !!practiceId && enabled,
    // Staff change rarely; the Staff screen is the only writer.
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  return { users: q.data || EMPTY, error: q.error || null };
}
